'use client'

import { useEffect, useState } from 'react'
import { getSupabaseClient } from '@/lib/supabaseClient'
import { downloadProgressFromServer, mergeProgress } from '@/utils/progressSync'

/**
 * 學習進度載入組件
 * 在用戶登入後自動從後端載入學習進度，並合併到本地 localStorage
 */
export default function ProgressLoader() {
  const [isLoading, setIsLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)

  useEffect(() => {
    // 只在客戶端執行
    if (typeof window === 'undefined') return

    // 避免重複載入
    if (hasLoaded) return

    const loadProgress = async () => {
      try {
        // 檢查用戶是否登入
        const supabase = getSupabaseClient()
        const { data: { session } } = await supabase.auth.getSession()

        if (!session?.user) {
          // 未登入，不載入
          return
        }

        // 檢查是否已經載入過（避免重複載入）
        const loadedFlag = localStorage.getItem('progress_loaded_from_server')
        if (loadedFlag === 'true') {
          setHasLoaded(true)
          return
        }

        setIsLoading(true)

        // 從後端下載學習進度
        const result = await downloadProgressFromServer()

        if (!result.success || !result.data) {
          console.warn('[Progress Loader] 下載進度失敗或無資料')
          setHasLoaded(true)
          return
        }

        const { data: remoteProgress } = result

        // 合併本地和遠端進度
        // 1. 單字進度
        if (remoteProgress.vocabulary) {
          const localVocab = localStorage.getItem('vocabularyProgress')
          const localVocabData = localVocab ? JSON.parse(localVocab) : {}
          const mergedVocab = mergeProgress(localVocabData, remoteProgress.vocabulary, 'vocabulary')
          localStorage.setItem('vocabularyProgress', JSON.stringify(mergedVocab))
        }

        // 2. 漢字進度
        if (remoteProgress.kanji) {
          const localKanji = localStorage.getItem('kanji-progress')
          const localKanjiData = localKanji ? JSON.parse(localKanji) : {}
          const mergedKanji = mergeProgress(localKanjiData, remoteProgress.kanji, 'kanji')
          localStorage.setItem('kanji-progress', JSON.stringify(mergedKanji))
        }

        // 3. 文法進度
        if (remoteProgress.grammar) {
          const localGrammar = localStorage.getItem('grammar-progress')
          const localGrammarData = localGrammar ? JSON.parse(localGrammar) : {}
          const mergedGrammar = mergeProgress(localGrammarData, remoteProgress.grammar, 'grammar')
          localStorage.setItem('grammar-progress', JSON.stringify(mergedGrammar))
        }

        // 4. 五十音進度
        if (remoteProgress.gojuon) {
          const localGojuon = localStorage.getItem('gojuonProgress')
          const localGojuonData = localGojuon ? JSON.parse(localGojuon) : { hiragana: {}, katakana: {} }
          const mergedGojuon = {
            hiragana: mergeProgress(localGojuonData.hiragana || {}, remoteProgress.gojuon.hiragana || {}, 'gojuon'),
            katakana: mergeProgress(localGojuonData.katakana || {}, remoteProgress.gojuon.katakana || {}, 'gojuon')
          }
          localStorage.setItem('gojuonProgress', JSON.stringify(mergedGojuon))
        }

        // 標記已載入
        localStorage.setItem('progress_loaded_from_server', 'true')

        if (process.env.NODE_ENV === 'development') {
          console.log('[Progress Loader] 學習進度載入完成')
        }

      } catch (error) {
        console.error('[Progress Loader] 載入進度失敗:', error)
      } finally {
        setIsLoading(false)
        setHasLoaded(true)
      }
    }

    // 延遲載入，避免影響頁面初始載入
    const timer = setTimeout(() => {
      loadProgress()
    }, 2000)

    return () => clearTimeout(timer)
  }, [hasLoaded])

  // 這個組件不渲染任何內容
  return null
}

